import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io, { Socket, SocketOptions } from 'socket.io-client';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { useServerTimeStore } from '@/zustand/server-time/store.ts';
import { TimeSpan } from '@/utility/time-span.ts';
import { SOCKET_EVENTS } from '@/constants/socket.ts';

type SocketContextType = {
	socket: Socket | null;
	isConnected: boolean;
	emit: (event: string, ...args: any[]) => void;
	on: (event: string, callback: (...args: any[]) => void) => void;
	off: (event: string, callback?: (...args: any[]) => void) => void;
};

const SocketContext = createContext<SocketContextType | null>(null);

export const useSocket = (): SocketContextType => {
	const socketContext = useContext(SocketContext);

	if (!socketContext) {
		throw new Error('useSocket must be used within a SocketProvider');
	}

	return socketContext;
};

type Props = {
	options?: SocketOptions;
	children: React.ReactNode;
};

export const SocketProvider: React.FC<Props> = ({ options = {}, children }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const isDevelopment = import.meta.env.MODE === 'development';
	const { data: auth } = useAuthStore();
	const serverTimeStore = useServerTimeStore();

	const lastServerTimeJoinedDateRef = useRef<Date | null>(null);
	const calculateServerTimePerSecondTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const currentServerTimeRef = useRef<Date | null>(null);

	const updateServerTime = (serverTime: Date) => {
		serverTimeStore.setData({ serverTime });
	};

	const calculateCurrentServerTime = (serverTime: string) => {
		lastServerTimeJoinedDateRef.current = new Date();
		currentServerTimeRef.current = new Date(serverTime);

		if (calculateServerTimePerSecondTimeoutRef?.current) {
			clearTimeout(calculateServerTimePerSecondTimeoutRef.current);
		}

		calculatePerSecondServerTime();
	};

	const calculatePerSecondServerTime = () => {
		calculateServerTimePerSecondTimeoutRef.current = setTimeout(() => {
			if (lastServerTimeJoinedDateRef.current && currentServerTimeRef.current) {
				const currentDate = new Date();
				const lastServerTimeJoinedandCurrentDateDiff = currentDate.getTime() - lastServerTimeJoinedDateRef.current.getTime();

				const newServerTime = new Date(currentServerTimeRef.current.getTime() + lastServerTimeJoinedandCurrentDateDiff);

				if (newServerTime) {
					updateServerTime(newServerTime);
				}
			}

			calculatePerSecondServerTime();
		}, TimeSpan.milliSecond.oneThousand);
	};

	useEffect(() => {
		let socketInstance: Socket | null = null;

		if (!auth?.access_token) {
			socket?.disconnect();
		}

		if (auth?.access_token) {
			const socketUri: string = process.env.SOCKET_URL || '';

			socketInstance = io(socketUri, {
				...options,
				transports: ['websocket'],
				autoConnect: true,
				secure: true,
				reconnection: true,
				reconnectionAttempts: Infinity,
				reconnectionDelayMax: 10000,
				auth: {
					token: auth?.access_token
				}
			});

			socketInstance.on('connect', () => {
				if (isDevelopment) {
					console.log('Socket.IO bağlantısı başarılı', socketUri);
				}

				setIsConnected(true);
			});

			socketInstance.on('disconnect', () => {
				if (isDevelopment) {
					console.log('Socket.IO bağlantısı kesildi', socketUri);
				}

				setIsConnected(false);
			});

			socketInstance.on('connect_error', error => {
				if (isDevelopment) {
					console.error('Socket.IO bağlantı hatası:', error);
				}

				setIsConnected(false);
			});

			setSocket(socketInstance);
		}

		return () => {
			if (socketInstance) {
				setIsConnected(false);
			}
		};
	}, [auth?.access_token]);

	useEffect(() => {
		if (socket && isConnected) {
			updateServerTime(new Date());

			socket.on(SOCKET_EVENTS.ON.SERVER_TIME, data => {
				if (data?.serverTime) {
					calculateCurrentServerTime(data.serverTime);
				}
			});
		}

		return () => {
			socket?.off?.(SOCKET_EVENTS.ON.SERVER_TIME);
		};
	}, [socket, isConnected]);

	const value: SocketContextType = {
		socket,
		isConnected,
		emit: (event, ...args) => socket?.emit(event, ...args),
		on: (event, callback) => socket?.on(event, callback),
		off: (event, callback) => socket?.off(event, callback)
	};

	return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
